<template>
    <div
        :class="[
            $style.templateGridDetail,
            {
                [$style.templateGridDetailMobile]: isMobile
            }
        ]"
    >
        <div
            ref="picCard"
            :style="{ height: `${cardDymaticHeight}px` }"
            :class="[$style.card, $style.gridItem1]"
        >
            <img :src="imgSrc" />
        </div>

        <div :class="[$style.tags, $style.gridItem2]">
            <TemplateTag type="Template" size="medium"></TemplateTag>
            <template v-for="(item, index) in props.template?.showTags" :key="index">
                <div :class="$style.categroyTag">
                    <div>{{ $t(`experience.onboarding.tags.${item}`) }}</div>
                    <pt-icon icon="pt-icon-tagleft" :class="$style.icon"></pt-icon>
                </div>
            </template>
        </div>

        <div :class="[$style.title, $style.gridItem3]">
            <h3>{{ props.template?.i18nName }}</h3>
        </div>

        <p :class="[$style.desc, $style.gridItem4]">{{ props.template?.i18nDesc }}</p>

        <div :class="[$style.button, $style.gridItem5]">
            <pt-button size="medium" @click="handleUseTemplate">
                {{ $t('experience.onboarding.use_template') }}
            </pt-button>
        </div>
    </div>
</template>

<script lang="ts">
import TemplateTag from '@experience/components/template/TemplateTag.vue';
export default {
    name: 'TemplateDetail',
    components: {
        TemplateTag
    }
};
</script>

<script lang="ts" setup>
import { computed, ref, onMounted, nextTick } from 'vue';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
// interface
interface Props {
    src: string;
    template: ExperienceTemplateV4;
}
// props
const props = defineProps<Props>();
// emits
const emit = defineEmits(['use']);
let resizeTimer = 0;

const picCard = ref(null);
const cardDymaticHeight = ref(0);
// state

const isMobile = ref(document.documentElement.clientWidth <= 1000);

onMounted(() => {
    nextTick(() => {
        cardDymaticHeight.value = picCard.value?.clientWidth / 1.268298;
    });

    window.addEventListener('resize', () => {
        isMobile.value = document.documentElement.clientWidth <= 1000;

        clearTimeout(resizeTimer);

        resizeTimer = setTimeout(() => {
            cardDymaticHeight.value = picCard.value?.clientWidth / 1.268298;
        }, 100);
    });
});

// computed
const imgSrc = computed(() => {
    return props.template?.pcThumbnail || props.template?.mobileThumbnail;
});
// watch

// onMounted

// onUnmounted

// function
function handleUseTemplate() {
    emit('use');
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.templateGridDetail {
    display: flex;
}

.templateGridDetail {
    width: 100%;
    display: inline-grid;
    grid-template-columns: calc(50% - 26px) 50%;
    grid-column-gap: 4em;
    grid-row-gap: 20px;
    justify-content: center;
    max-width: 980px;
    padding-bottom: 20px;
    margin: 0 auto;

    .gridItem1 {
        grid-row-start: 1;
        grid-row-end: 6;
        margin: 0 auto;
        margin-left: 12px;
    }

    .gridItem2 {
        max-width: 700px;
        min-width: 320px;
        align-content: flex-start;
        margin-left: 12px;
    }

    .gridItem3 {
        max-width: 700px;
        min-width: 320px;
        margin-left: 12px;
    }

    .gridItem4 {
        max-width: 700px;
        min-width: 320px;
        margin-left: 12px;
    }

    .gridItem5 {
        justify-items: center center;
        max-width: 700px;
        min-width: 320px;
        display: flex;
        align-items: flex-end;
        margin-left: 12px;
    }

    .card {
        width: 100%;
        padding: 42px;
        background-color: $pt-black-30;
        border-radius: 8px;
        display: flex;
        align-items: center;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .tags {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: flex-start;

        span {
            margin-right: 16px;
            align-content: center;
        }

        .categroyTag {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            margin: 5px 20px;
            margin-left: 0;
            margin-right: 8px;

            .icon {
                width: 24px;
                height: 24px;
                fill: $pt-black-30;
                margin-right: -4px;
                transform: rotate(180deg);
                position: relative;
                left: -4px;
            }

            div {
                height: 24px;
                width: auto;
                line-height: 24px;
                font-size: 12px;
                color: $pt-black-300;
                background-color: $pt-black-30;
                padding: 0px 6px 0 10px;
                border-radius: 3px 0 0 3px;
                text-align: center;
            }
        }
    }

    .title {
        font-size: 32px;
        color: $pt-black-900;
        height: auto;

        h3 {
            width: 100%;
            display: flex;
            justify-content: flex-start;
            flex-wrap: wrap;
            overflow: hidden;
            word-break: break-word;
            color: #091e42;
            font-weight: 500;
        }
    }

    .desc {
        font-size: 14px;
        color: $pt-black-900;
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
        overflow: hidden;
        word-break: break-word;
    }
}

.templateGridDetailMobile {
    grid-template-columns: 100%;
    justify-content: center;
    // grid-template-rows: repeat(1, 100%);
    grid-auto-flow: rows dense;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 20px;

    .gridItem2,
    .gridItem3,
    .gridItem4,
    .gridItem5 {
        max-width: none;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 20px;
        margin-left: 0;
    }

    .gridItem2 {
        justify-content: flex-start;
    }

    .gridItem3 {
        grid-row-start: 2;
    }

    .gridItem1 {
        grid-row-start: 3;
        grid-row-end: 5;
        margin-left: 0;
    }

    .gridItem5 {
        display: flex;
        align-items: center;
        justify-content: center;

        button {
            width: 80%;
        }
    }

    .title {
        h3 {
            text-align: center;
            justify-content: center;
        }
    }
}
</style>
